import { type ReactNode, type RefObject, useCallback, useRef, useState } from "react";
import { Popover } from "../Popover/Popover";
import { DropdownMenuEntry } from "./DropdownMenuEntry";
import { entryKey, type MenuEntry } from "./menuEntry";
import { useMenuRovingFocus } from "./useMenuRovingFocus";

export type { MenuAction, MenuEntry } from "./menuEntry";

export interface DropdownMenuProps {
  /** Render the trigger; spread the given props on a <button>. */
  trigger: (props: {
    ref: RefObject<HTMLButtonElement | null>;
    onClick: () => void;
    "aria-expanded": boolean;
    "aria-haspopup": "menu";
  }) => ReactNode;
  items: MenuEntry[];
  align?: "start" | "end";
  minWidth?: number;
  ariaLabel?: string;
}

const DEFAULT_MIN_WIDTH = 200;

/** Menu button with roving focus (arrows, Home/End), Escape and outside click to close. */
export function DropdownMenu({ trigger, items, align = "end", minWidth = DEFAULT_MIN_WIDTH, ariaLabel }: DropdownMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const onKeyDown = useMenuRovingFocus(open, close);

  return (
    <>
      {trigger({ ref: anchorRef, onClick: () => setOpen((v) => !v), "aria-expanded": open, "aria-haspopup": "menu" })}
      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={close}
        align={align}
        minWidth={minWidth}
        role="menu"
        ariaLabel={ariaLabel}
        onKeyDown={onKeyDown}
      >
        {items.map((entry, i) => (
          <DropdownMenuEntry key={entryKey(entry, i)} entry={entry} onClose={close} />
        ))}
      </Popover>
    </>
  );
}
